import { useState } from 'react';
import { ApiError } from '../api/client';
import { useCreatePairingCode, useDevices, useRevokeDevice, useUsers } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { EmptyState, ErrorBanner, Modal, TableSkeleton, relativeTime } from '../components/ui';
import type { AgentDevice } from '../api/types';

/**
 * Paired phones.
 *
 * A counsellor sees their own; anyone with user.manage can see and pair
 * everyone's, because onboarding a phone happens standing next to the person.
 */
export function DevicesPage() {
  const { user } = useAuth();
  const canManageUsers = user?.permissions.includes('user.manage') ?? false;
  const [showAll, setShowAll] = useState(canManageUsers);
  const { data: devices = [], isPending } = useDevices(showAll && canManageUsers);
  const revoke = useRevokeDevice();
  const [pairingFor, setPairingFor] = useState<{ id?: string; name: string } | null>(null);
  const [error, setError] = useState<unknown>(null);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Phones</h1>
          <p className="sub">
            A paired Android phone dials leads on its own SIM, so the lead sees a local number and
            the call costs nothing. Outcomes sync back automatically.
          </p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-primary" onClick={() => setPairingFor({ name: 'your phone' })}>
            + Pair my phone
          </button>
        </div>
      </div>

      <ErrorBanner error={error} />

      {canManageUsers && (
        <label className="field" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Show everyone's phones
        </label>
      )}

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Phone</th>
              {showAll && canManageUsers && <th>Agent</th>}
              <th>Status</th>
              <th>Setup</th>
              <th>Last seen</th>
              <th>App</th>
              <th />
            </tr>
          </thead>
          {isPending ? (
            <TableSkeleton cols={showAll && canManageUsers ? 7 : 6} />
          ) : (
            <tbody>
              {devices.map((device) => (
                <tr key={device.id} style={device.revoked ? { opacity: 0.5 } : undefined}>
                  <td>
                    <strong>{device.label}</strong>
                    {device.android_version && (
                      <div className="hint" style={{ margin: 0 }}>Android {device.android_version}</div>
                    )}
                  </td>
                  {showAll && canManageUsers && <td>{device.user_name ?? '—'}</td>}
                  <td><DeviceStatus device={device} /></td>
                  <td><SetupState device={device} /></td>
                  <td>{device.last_seen_at ? relativeTime(device.last_seen_at) : <span style={{ color: 'var(--faint)' }}>never</span>}</td>
                  <td className="mono">{device.app_version ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {!device.revoked && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--danger)' }}
                        onClick={async () => {
                          if (!confirm(`Unpair ${device.label}? It will stop receiving calls immediately.`)) return;
                          try {
                            await revoke.mutateAsync(device.id);
                          } catch (err) {
                            setError(err);
                          }
                        }}
                      >
                        Unpair
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>

        {!isPending && devices.length === 0 && (
          <EmptyState
            title="No phones paired"
            action={
              <button className="btn btn-primary" onClick={() => setPairingFor({ name: 'your phone' })}>
                Pair a phone
              </button>
            }
          >
            Install the Sisya Dialler app, then pair it with a code from here.
          </EmptyState>
        )}
      </div>

      {canManageUsers && <PairForSomeoneElse onPick={(id, name) => setPairingFor({ id, name })} />}
      {pairingFor && (
        <PairingModal target={pairingFor} onClose={() => setPairingFor(null)} />
      )}
    </div>
  );
}

/**
 * "Reachable" is the distinction that matters operationally: a paired phone
 * that has not registered for push cannot be sent a call, and the agent will
 * get a confusing failure unless it is called out here.
 */
function DeviceStatus({ device }: { device: AgentDevice }) {
  if (device.revoked) return <span className="chip chip-dup">Unpaired</span>;
  if (!device.reachable) {
    return (
      <span className="chip" style={{ color: 'var(--warn)', background: 'var(--sunken)' }}
            title="Paired, but not registered for push yet. Open the app on the phone once.">
        <span className="dot" /> Not reachable
      </span>
    );
  }
  return (
    <span className="chip" style={{ color: 'var(--good)', background: 'var(--sunken)' }}>
      <span className="dot" /> Ready
    </span>
  );
}

/**
 * What the handset says about its own setup.
 *
 * The distinction that matters operationally is "not reported" versus
 * "reported a problem": a phone that has never checked in is running an old
 * build or has not been opened, which is a different conversation from one
 * that is actively misconfigured. Neither is inferable from the server side,
 * which is why the app reports it.
 */
function SetupState({ device }: { device: AgentDevice }) {
  if (device.revoked) return <span style={{ color: 'var(--faint)' }}>—</span>;

  if (device.setup_ready === null) {
    return (
      <span style={{ color: 'var(--faint)' }} title="This phone has never reported its setup. Open the app on it once.">
        Not reported
      </span>
    );
  }

  if (!device.setup_ready) {
    const missing = (device.setup_detail ?? '')
      .split(',')
      .filter((p) => p.endsWith(':missing'))
      .map((p) => p.split(':')[0])
      .join(', ');
    return (
      <span className="chip" style={{ color: 'var(--danger)', background: 'var(--sunken)' }}
            title={missing ? `Missing: ${missing}` : device.setup_detail ?? ''}>
        <span className="dot" /> Needs setup
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span className="chip" style={{ color: 'var(--good)', background: 'var(--sunken)' }}>
        <span className="dot" /> Ready
      </span>
      {/* An OPPO/Xiaomi/Vivo handset can report ready and still be throttled by
          a battery setting the app cannot inspect — worth flagging rather than
          letting an admin assume it is proven. */}
      {device.needs_oem_setup && (
        <span
          className="hint"
          style={{ margin: 0 }}
          title={`${device.manufacturer ?? 'This manufacturer'} overrides Android's battery settings. Confirm Battery usage → Allow background activity is On.`}
        >
          check battery
        </span>
      )}
    </span>
  );
}

function PairForSomeoneElse({ onPick }: { onPick: (id: string, name: string) => void }) {
  const { data: users = [] } = useUsers();
  const [selected, setSelected] = useState('');

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="card-head"><h2>Pair a phone for someone else</h2></div>
      <div className="card-body">
        <p className="hint" style={{ marginTop: 0 }}>
          Generates a code for another agent — for setting up their handset with them.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={selected} onChange={(e) => setSelected(e.target.value)}>
            <option value="">Choose an agent…</option>
            {users.filter((u) => u.is_active).map((u) => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </select>
          <button
            className="btn"
            disabled={!selected}
            onClick={() => {
              const u = users.find((x) => x.id === selected);
              if (u) onPick(u.id, u.full_name);
            }}
          >
            Generate code
          </button>
        </div>
      </div>
    </div>
  );
}

function PairingModal({
  target,
  onClose,
}: {
  target: { id?: string; name: string };
  onClose: () => void;
}) {
  const createCode = useCreatePairingCode();
  const [code, setCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  async function generate() {
    setError(null);
    try {
      const result = await createCode.mutateAsync(target.id);
      setCode(result.code);
      setExpiresAt(result.expires_at);
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    }
  }

  return (
    <Modal
      title={`Pair ${target.name}`}
      onClose={onClose}
      footer={<button className="btn" onClick={onClose}>Done</button>}
    >
      <ErrorBanner error={error} />
      <ol style={{ paddingLeft: 20, lineHeight: 1.7 }}>
        <li>Install the <strong>Sisya Dialler</strong> app on the phone.</li>
        <li>Open it and grant the phone and call-log permissions.</li>
        <li>Enter the code below and tap <strong>Pair</strong>.</li>
      </ol>

      {code ? (
        <div className="field">
          <div
            className="mono"
            style={{ fontSize: 34, letterSpacing: 6, textAlign: 'center', padding: '14px 0' }}
          >
            {code}
          </div>
          <div className="hint" style={{ textAlign: 'center' }}>
            {/* Short-lived on purpose: until it is used, this code is worth as
                much as the credential it hands out. */}
            Single use. Expires {expiresAt ? new Date(expiresAt).toLocaleTimeString() : 'shortly'}.
          </div>
        </div>
      ) : (
        <button className="btn btn-primary" onClick={generate} disabled={createCode.isPending}>
          {createCode.isPending ? <span className="spinner" /> : 'Generate pairing code'}
        </button>
      )}
    </Modal>
  );
}
